import * as React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BeatLoader from 'react-spinners/BeatLoader';
import {
  Wrapper,
  Welcome,
  Title,
  Form,
  Label,
  Input,
  LoginButton,
} from '../studentLoginform/styles';
import { academicYear } from '../../../../helpers';
import { useExamsTypeAndSem } from '../../../../helpers/examTypeAndSemester';

/**
 * InvigilatorLoginForm
 *
 * Login form for invigilators to access their examination timetable.
 * - Accepts a passcode.
 * - On successful login, stores token and redirects to invigilator dashboard.
 * - Shows loading spinner and error/success toasts.
 *
 * @param toggleModal - Function to close the modal after successful login.
 */
export const InvigilatorLoginForm = ({ toggleModal }: { toggleModal: () => void }): JSX.Element => {
  // State for passcode input
  const [passcode, setPasscode] = React.useState<string>('');
  // For navigation after login
  const navigate = useNavigate();
  // Ref to focus the input if needed
  const passcodeInputRef = React.useRef<HTMLInputElement>(null);
  // Custom hook to get exam type and semester
  const examTypeAndSem = useExamsTypeAndSem();

  // GraphQL mutation for invigilator sign-in
  const SIGN_IN_INVIGILATOR = gql`
    mutation signInvigilator($passcode: String!) {
      signInInvigilator(passcode: $passcode) {
        token
      }
    }
  `;

  // useMutation hook for sign-in
  const [signInvigilator, { data, loading }] = useMutation(SIGN_IN_INVIGILATOR);

  // Effect: if login is successful, store token, close modal, and redirect
  React.useEffect(() => {
    const token = data?.signInInvigilator?.token;
    if (data && token) {
      localStorage.setItem('coe-invigilator-token', token);
      toggleModal();
      navigate('/invigilator', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /**
   * Handles login button click.
   * - Validates input.
   * - Calls sign-in mutation.
   * - Shows error toast on failure.
   */
  const handleInvigilatorLogin = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.preventDefault();
    if (passcode === '') {
      toast.warn('Please enter passcode first!');
      passcodeInputRef.current && passcodeInputRef.current.focus();
      return;
    }
    try {
      await signInvigilator({ variables: { passcode } });
    } catch (err) {
      toast.error(err.message || `Incorrect passcode. Contact CoE Examiner`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Render the login form UI
  return (
    <Wrapper>
      <Welcome>Welcome</Welcome>
      <Title>
        {/* Exam type, semester, and academic year info */}
        {/* eslint-disable-next-line max-len*/}
        Log in to view your {examTypeAndSem && examTypeAndSem.examType.toLowerCase()}{' '}
        {examTypeAndSem?.semester} examination timetable for the {academicYear} academic year.
      </Title>
      <Form>
        <Label>Passcode</Label>
        <Input
          placeholder="Enter your passcode"
          className="focus:bg-gray-100"
          onChange={(e) => {
            setPasscode(e.target.value);
          }}
          value={passcode}
          ref={passcodeInputRef}
        />
        <LoginButton style={{ color: 'white' }} onClick={handleInvigilatorLogin}>
          {' '}
          {loading ? <BeatLoader color="#fff" size={18} /> : 'Log in'}
        </LoginButton>
      </Form>
    </Wrapper>
  );
};